// @ts-nocheck

import React, { Component, Suspense } from 'react';
import { BrowserRouter } from 'react-router-dom';
import * as moment from 'moment';
import 'moment/locale/pt-br';
import './load.scss';
import '../utils/utils.scss';
import { ComponentErrorBoundary } from '../components/ErrorBoundary/ErrorBoundary';
import SmartErrorBoundary from '../components/ErrorBoundary/SmartErrorBoundary';
import ConnectivityMonitor from '../components/ConnectivityMonitor/ConnectivityMonitor';
import VersionDetector from '../components/VersionDetector/VersionDetector';

const Router = React.lazy(() => import('./Router'));

moment.locale('pt-br');

// ===================================================
// 🛡️ ERROR BOUNDARY COMPLETO
// ===================================================

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  retryCount: number;
}

class App extends Component<any, ErrorBoundaryState> {
  
  constructor(props: any) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0
    };
  }

  // ✅ IMPLEMENTAÇÃO OBRIGATÓRIA PARA ERROR BOUNDARY
  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    console.error('🛡️ [ErrorBoundary] getDerivedStateFromError:', error);
    return {
      hasError: true,
      error
    };
  }

  // ✅ CAPTURA DETALHADA DE ERROS
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('💥 [App] ERRO CRÍTICO capturado no App:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
      retryCount: this.state.retryCount + 1
    });

  }

  // ✅ FUNÇÃO DE RETRY
  handleRetry = () => {
    if (this.state.retryCount < 3) {
      console.log('🔄 [ErrorBoundary] Tentando recuperar aplicação...');
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null
      });
    } else {
      console.error('❌ [ErrorBoundary] Máximo de tentativas atingido, recarregando página...');
      window.location.reload();
    }
  };

  // ✅ UI DE FALLBACK ELEGANTE
  renderErrorFallback() {
    const { error, errorInfo, retryCount } = this.state;
    
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px',
        backgroundColor: '#f5f5f5',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{
          maxWidth: '600px',
          backgroundColor: 'white',
          padding: '30px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h1 style={{ color: '#d32f2f', marginBottom: '20px' }}>
            🚨 Ops! Algo deu errado
          </h1>
          
          <p style={{ color: '#666', marginBottom: '20px', lineHeight: 1.5 }}>
            A aplicação encontrou um erro inesperado. Nossa equipe foi notificada.
          </p>

          {retryCount < 3 ? (
            <button
              onClick={this.handleRetry}
              style={{
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px',
                marginRight: '10px'
              }}
            >
              🔄 Tentar Novamente ({retryCount}/3)
            </button>
          ) : (
            <button
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: '#d32f2f',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              🔄 Recarregar Página
            </button>
          )}

          {/* ✅ DETALHES TÉCNICOS (apenas em desenvolvimento) */}
          {process.env.NODE_ENV === 'development' && error && (
            <details style={{ marginTop: '20px', textAlign: 'left' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                📋 Detalhes Técnicos
              </summary>
              <pre style={{
                backgroundColor: '#f5f5f5',
                padding: '10px',
                borderRadius: '4px',
                fontSize: '12px',
                overflow: 'auto',
                maxHeight: '200px'
              }}>
                <strong>Erro:</strong> {error.message}
                {'\n\n'}
                <strong>Stack:</strong> {error.stack}
                {errorInfo && (
                  <>
                    {'\n\n'}
                    <strong>Component Stack:</strong> {errorInfo.componentStack}
                  </>
                )}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }

  render() {
    // ✅ RENDERIZAR FALLBACK EM CASO DE ERRO
    if (this.state.hasError) {
      return this.renderErrorFallback();
    }

    // ✅ RENDERIZAÇÃO NORMAL COM UX MELHORADA
    return (
      <BrowserRouter basename="/">
        {/* 🧠 Smart Error Boundary para App inteiro */}
        <SmartErrorBoundary 
          level="app" 
          contextInfo={{ appVersion: '2.0', userAgent: navigator.userAgent }}
        >
          {/* 🌐 Connectivity Monitor global */}
          <ConnectivityMonitor />
          
          {/* 🔄 Version Detector global */}
          <VersionDetector />
          
          <Suspense fallback={<div className='center'><div className="loader"></div><div className='break'>Carregando...</div></div>}>
            <Router />
            
            {/* {process.env.NODE_ENV === 'development' && (
              <ComponentErrorBoundary name="PerformanceMonitor">
                <PerformanceMonitor />
              </ComponentErrorBoundary>
            )} */}
            
          </Suspense>
        </SmartErrorBoundary>
      </BrowserRouter>
    );
  }
}

export default App;
